import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Download, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { inr } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Products() {
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState({});
  const fileRef = useRef(null);
  const navigate = useNavigate();

  const load = () => {
    const params = {};
    if (q) params.q = q;
    if (status !== "all") params.status = status;
    api.get("/admin/products", { params }).then((r) => { setProducts(r.data); setSelected({}); }).catch((e) => toast.error(errMsg(e)));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, status]);

  const del = async (id) => {
    if (!window.confirm("Delete this product permanently?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Product deleted");
    load();
  };

  const selCount = Object.values(selected).filter(Boolean).length;
  const allChecked = products.length > 0 && selCount === products.length;

  const toggleAll = () => {
    if (allChecked) setSelected({});
    else setSelected(Object.fromEntries(products.map((p) => [p.id, true])));
  };

  const bulkDelete = async (all = false) => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!all && !ids.length) return;
    const msg = all
      ? `DELETE ALL ${products.length} products permanently? This cannot be undone.`
      : `Delete ${ids.length} selected product(s) permanently?`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.post("/admin/products-bulk-delete", all ? { all: true } : { product_ids: ids });
      toast.success(`Deleted ${r.data.deleted} product(s)`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const exportCsv = async () => {
    const r = await api.get("/admin/products-export", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = "products.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/admin/products-import", fd);
      toast.success(`Import complete: ${r.data.created} created, ${r.data.updated} updated`);
      load();
    } catch (err) { toast.error(errMsg(err)); }
    e.target.value = "";
  };

  return (
    <div className="space-y-4" data-testid="admin-products-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-heading text-2xl font-bold tracking-tight mr-auto">Products</h1>
        <Button variant="outline" size="sm" onClick={exportCsv} data-testid="export-csv-btn"><Download size={15} className="mr-1" /> Export CSV</Button>
        <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="import-csv-btn"><Upload size={15} className="mr-1" /> Import CSV</Button>
        <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={importCsv} data-testid="import-csv-input" />
        <Button size="sm" className="bg-stone-900 hover:bg-brand text-white btn-press" onClick={() => navigate("/admin/products/new")} data-testid="new-product-btn">
          <Plus size={15} className="mr-1" /> New Product
        </Button>
        {selCount > 0 && (
          <Button variant="outline" size="sm" className="border-red-300 text-red-600" onClick={() => bulkDelete(false)} data-testid="bulk-delete-btn">
            <Trash2 size={15} className="mr-1" /> Delete {selCount} selected
          </Button>
        )}
        {products.length > 0 && (
          <Button variant="outline" size="sm" className="border-red-300 text-red-600" onClick={() => bulkDelete(true)} data-testid="delete-all-btn">
            <Trash2 size={15} className="mr-1" /> Delete all
          </Button>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        <Input placeholder="Search products…" value={q} onChange={(e) => setQ(e.target.value)} className="w-64 bg-white" data-testid="product-search-input" />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-36 bg-white" data-testid="product-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-neutral-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="text-left text-[11px] font-mono uppercase tracking-widest text-neutral-500 border-b border-neutral-200">
              <th className="p-3 w-8"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="select-all-checkbox" /></th>
              <th className="p-3">Product</th><th className="p-3">Category</th><th className="p-3">Metal</th>
              <th className="p-3">Live Price</th><th className="p-3">Stock</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {products.map((p) => {
              const stock = (p.variants || []).reduce((s, v) => s + (v.stock || 0), 0);
              return (
                <tr key={p.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`admin-product-row-${p.slug}`}>
                  <td className="p-3"><input type="checkbox" checked={!!selected[p.id]} onChange={(e) => setSelected({ ...selected, [p.id]: e.target.checked })} data-testid={`select-product-${p.slug}`} /></td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {p.images?.[0] && <img src={p.images[0]} alt="" className="h-10 w-10 object-cover border border-neutral-200" />}
                      <div>
                        <Link to={`/admin/products/${p.id}`} className="font-medium hover:text-brand">{p.title}</Link>
                        <p className="text-xs font-mono text-neutral-400">{p.slug}{p.personalisation?.enabled ? " · ✦ personalised" : ""}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-neutral-600">{p.category}</td>
                  <td className="p-3">
                    <span className={`text-[11px] font-mono uppercase px-2 py-0.5 ${p.metal === "gold" ? "bg-amber-100 text-amber-800" : "bg-neutral-200 text-neutral-600"}`}>
                      {p.purity} {p.metal}
                    </span>
                  </td>
                  <td className="p-3 font-medium" data-testid={`admin-price-${p.slug}`}>
                    {p.price_min != null ? `${inr(p.price_min)}${p.price_max > p.price_min ? ` – ${inr(p.price_max)}` : ""}` : "—"}
                  </td>
                  <td className={`p-3 font-mono ${stock <= 5 ? "text-brand font-semibold" : ""}`}>{stock}</td>
                  <td className="p-3">
                    <span className={`text-[11px] font-mono uppercase px-2 py-0.5 ${p.status === "active" ? "bg-emerald-100 text-emerald-800" : "bg-neutral-200 text-neutral-600"}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/admin/products/${p.id}`)} data-testid={`edit-product-${p.slug}`}>Edit</Button>
                    <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(p.id)} data-testid={`delete-product-${p.slug}`}><Trash2 size={15} /></Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {products.length === 0 && <p className="p-6 text-center text-sm text-neutral-500" data-testid="no-products">No products found.</p>}
      </div>
    </div>
  );
}
